import { IBasicEvent, IBasicFile } from '@sdk/contracts';
import { Button, Popconfirm } from 'antd';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { LiveRecordItemDocuments } from '../LiveRecordItemDocuments';

import {
  ContentWrapper,
  DescriptionWrapper,
  InformationWrapper,
  Title,
  TitleWrapper,
  Wrapper,
  VideoWrapper
} from './LiveRecordItem.styles';

import { Icon } from '@/components/Icon';
import { VideoPlayer } from '@/components/VideoPlayer';
import { useMutateEvents, useMySubscriptions, useProfile } from '@/store';
import { getLiveRecordRelativeTimeString, hasViewedLiveRecord } from '@/utils';

type Props = {
  recording: IBasicFile;
  event: IBasicEvent;
};

export const LiveRecordItem: React.FC<Props> = ({ recording, event }) => {
  const { selectedSubscription } = useMySubscriptions();
  const { addLiveRecordView, removeLiveRecord } = useMutateEvents();
  const { t } = useTranslation('CommonEventTranslations');
  const [searchParams] = useSearchParams();
  const {
    roleCheck: { isStudent },
    profile
  } = useProfile();

  const navigate = useNavigate();
  const actualPathname = useLocation().pathname;
  const sendEvent = () => {
    if (!isStudent || !selectedSubscription) return;
    addLiveRecordView({ eventId: event._id, liveRecordId: recording._id });
  };
  const hasConferenceData = event.conferenceData;
  const goToEvent = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    const fromValue = searchParams.toString()
      ? `${actualPathname}?${searchParams.toString()}`
      : actualPathname;
    newSearchParams.set('from', fromValue);
    navigate({
      pathname: `/calendar/${event._id}`,
      search: `?${newSearchParams.toString()}`
    });
  };
  const handleDelete = (e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    if (isStudent) return;
    removeLiveRecord({ eventId: event._id, liveRecordId: recording._id });
  };

  const renderCheckmark = () => {
    return isStudent && hasViewedLiveRecord(event, recording, profile?._id);
  };
  return (
    <Wrapper onClick={goToEvent}>
      <VideoWrapper onClick={e => e.stopPropagation()}>
        <VideoPlayer key={recording._id} file={recording} loadStrategy="play" onPlay={sendEvent} />
      </VideoWrapper>
      <InformationWrapper>
        <TitleWrapper>
          <Title>
            {renderCheckmark() && <Icon i="Checkmark" color="green6" />} {recording.name}
          </Title>

          {!isStudent && (
            <Popconfirm
              placement="topRight"
              title={t('LIVE_RECORD_DELETE_CONFIRMATION')}
              onConfirm={handleDelete}
              okText={t('CONFIRM_MODAL_EVENT_REMOVE_OK_TEXT')}
              cancelText={t('CONFIRM_MODAL_EVENT_REMOVE_CANCEL_TEXT')}>
              <Button onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}>
                <Icon i="TrashCan" />
              </Button>
            </Popconfirm>
          )}
        </TitleWrapper>
        <ContentWrapper>
          {hasConferenceData && (
            <>
              {t('RECORDED_PARTICIPANTS_DRAWER_TITLE', {
                count: event.conferenceData?.insights?.recordedParticipants?.length ?? 0
              })}
              {' · '}
            </>
          )}
          {getLiveRecordRelativeTimeString(recording.createdAt)}
        </ContentWrapper>
        <DescriptionWrapper>
          <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(event.description) }} />
        </DescriptionWrapper>
        {event.attachments.length > 0 && (
          <div onClick={e => e.stopPropagation()}>
            <LiveRecordItemDocuments attachments={event.attachments} />
          </div>
        )}
      </InformationWrapper>
    </Wrapper>
  );
};
